// react
import { MouseEvent, useContext, useState, ReactNode } from 'react';

// types
import { Entity, User, UserProject } from '../../../@types';

// context
import { ContextUser, ContextUserEntity } from '../../../App.context';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useNavigate } from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, Box, Button, Grid, InputLabel, SelectChangeEvent } from '@mui/material';

// forms
import { Select } from '../../fields/Select/Select';

// utils
import { parseJwt, toBase64Url } from '../../../utils/jwt';
import { translateRole, translateType } from '../../../utils/translations';

export const FormLoginPersona = () => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const navigate = useNavigate();
	const { t } = useTranslation();

	// context
	const { setUserEntity } = useContext(ContextUserEntity);
	const { setUser } = useContext(ContextUser);

	// state
	const [error, setError] = useState<string | null>(null);
	const [users, setUsers] = useState<User[]>([]);
	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
	const [userEntities, setUserEntities] = useState<Entity[]>([]);
	const [showEntitySelector, setShowEntitySelector] = useState<boolean>(false);

	// fetch users
	useFetch({
		url: `${process.env.REACT_APP_API_URL}/users?populate=entities`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setUsers(res.data);
			}
		},
	});

	// When user selection changes, update the available entities
	const handleUserChange = (e: SelectChangeEvent<unknown>, child: ReactNode) => {
		const userId = Number(e.target.value);
		const user = users.find((u) => u.id === userId);

		setSelectedUser(user || null);
		setSelectedEntity(null);

		if (user && user.entities) {
			// Filter and convert entities to proper Entity objects
			const entities = (user.entities || [])
				.filter((entity) => typeof entity === 'object')
				.map((entity) => entity as Entity);

			setUserEntities(entities);
			setShowEntitySelector(entities.length > 1);

			// If there's only one entity, preselect it
			if (entities.length === 1) {
				setSelectedEntity(entities[0]);
			} else {
				setSelectedEntity(null);
			}
		} else {
			setUserEntities([]);
			setShowEntitySelector(false);
		}
	};

	const handleEntityChange = (e: SelectChangeEvent<unknown>, child: ReactNode) => {
		const entityId = Number(e.target.value);
		const entity = userEntities.find((entity) => entity.id === entityId);
		setSelectedEntity(entity || null);
	};

	const handleSubmitPersona = (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!selectedUser) {
			setError(t('error.selectUser'));
			return;
		}

		if (showEntitySelector && !selectedEntity) {
			setError(t('error.selectEntity'));
			return;
		}

		// Check if user has any active projects
		const userProjects = (selectedUser.projects as UserProject[]) || [];
		const hasActiveProject = userProjects.some(project => 
			project.status && project.status !== 'DEACTIVATED'
		);
		
		if (!hasActiveProject) {
			setError(t('error.userDeactivated'));
			return;
		}

		if (accessToken && idToken) {
			// get payloads
			const { payload: accessTokenPayload } = parseJwt(accessToken);
			const { payload: idTokenPayload } = parseJwt(idToken);

			// define token arrays
			const accessTokenArray = accessToken.split('.');
			const idTokenArray = idToken.split('.');

			// construct new access token payload
			const accessTokenPayloadNew = toBase64Url({
				...accessTokenPayload,
				// non sso users have email in token instead of upn
				email: selectedUser.email,
			});

			// construct new id token payload
			const idTokenPayloadNew = toBase64Url({
				...idTokenPayload,
				roles: [process.env.REACT_APP_AZURE_ROLE_GENERAL],
			});

			// define new tokens (header and payload)
			let accessTokenNew = `${accessTokenArray[0]}.${accessTokenPayloadNew}`;
			let idTokenNew = `${idTokenArray[0]}.${idTokenPayloadNew}`;

			// add signatures if they exist
			if (accessTokenArray[2]) {
				accessTokenNew += `.${accessTokenArray[2]}`;
			}

			if (idTokenArray[2]) {
				idTokenNew += `.${idTokenArray[2]}`;
			}

			// set user and jwt in session storage
			sessionStorage.setItem('accessToken', accessTokenNew);
			sessionStorage.setItem('idToken', idTokenNew);
			sessionStorage.setItem(
				'user',
				JSON.stringify({
					...selectedUser,
					isPersona: true,
				})
			);

			setUser(selectedUser);

			// Set the selected entity or the only entity available
			const entityToSet = selectedEntity || (userEntities.length === 1 ? userEntities[0] : null);

			if (entityToSet) {
				setUserEntity(entityToSet);
				sessionStorage.setItem('userEntityId', String(entityToSet.id));
			}

			// navigate to project
			navigate(`/projects`);
		}
	};

	const optionsUsers = users.map((user) => {
		// Get entity information for display in the dropdown
		const firstEntity =
			user.entities && user.entities.length > 0
				? typeof user.entities[0] === 'object'
					? (user.entities[0] as Entity)
					: null
				: null;

		return {
			label: `${user.firstName} ${user.lastName}`,
			secondary:
				user.entities && user.entities.length > 1
					? t('multipleEntities', { count: user.entities.length })
					: firstEntity
					? translateType(firstEntity.type, t)
					: translateRole(user.role, t),
			value: user.id,
		};
	});

	const optionsEntities = userEntities.map((entity) => {
		return {
			label: entity.name,
			secondary: translateType(entity.type, t),
			value: entity.id,
		};
	});

	return (
		<form id="form-login-persona" onSubmit={handleSubmitPersona}>
			<Box>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<InputLabel htmlFor="form-login-persona-user" required={true}>
							{t('selectPersona')}
						</InputLabel>
						<Select
							id="form-login-persona-user"
							name="user"
							options={optionsUsers}
							required={true}
							onChange={handleUserChange}
						/>
					</Grid>

					{showEntitySelector && (
						<Grid item xs={12}>
							<InputLabel htmlFor="form-login-persona-entity" required={true}>
								{t('selectEntity')}
							</InputLabel>
							<Select
								id="form-login-persona-entity"
								name="entity"
								options={optionsEntities}
								required={true}
								onChange={handleEntityChange}
							/>
						</Grid>
					)}

					{error && (
						<Grid item xs={12}>
							<Alert severity="error">{error}</Alert>
						</Grid>
					)}
					<Grid item xs={12}>
						<Button fullWidth={true} type="submit" variant="contained">
							{t('signIn')}
						</Button>
					</Grid>
				</Grid>
			</Box>
		</form>
	);
};
